import { useTranslation } from 'react-i18next';
import List from '@mui/material/List';

import InventoryIcon from '@mui/icons-material/Inventory';
import SimCardIcon from 'assets/images/simprofile.svg';
import BPUploadIcon from 'assets/images/BPUploadIcon.svg';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocationsIcon from 'assets/images/Locations.svg';
import { NavigationLink, NavigationLinkWithInnerNavigationSupport } from './NavigationLink';
import { useHasFeatureFlag } from 'src/featureFlags/useHasFeatureFlag';
import { SimInventoryCan } from 'src/permissions/PermissionProvider';
import { Actions, Subjects } from 'src/permissions/ability';
import { useAbility } from 'src/permissions/hooks/useAbility';
import {
  adminNavigationAtom,
  productNavigationAtom,
  PAGES,
  activityLogNavigationAtom,
} from 'src/atoms/pages/pages';
import { useSimProfilesUrlFromFilters } from 'src/simInventory/hooks/useSimProfilesUrlFromFilters';
import { useBulkProvisioningUrlFromFilters } from '@/activityLog/bulkProvisioning/Hooks/useBulkProvisioningUrlFromFilters';
import { useDashboardUrlFromFilters } from 'src/dashboard/hooks/useDashboardUrlFromFilters';
import { useLocationsUrlFromFilters } from 'src/locations/hooks/useLocationsUrlFromFilters';
import { useActivityLogAvailable } from '@/activityLog/common/hooks/useActivityLogAvailable.ts';

export const NavigationMenu = () => {
  const ability = useAbility();
  const { t } = useTranslation();

  const locationsEnabled = useHasFeatureFlag('Locations');
  const productsManagementEnabled = useHasFeatureFlag('ProductManagement');
  const activityLogEnabled = useActivityLogAvailable();

  const adminPanelEnabled =
    ability.can(Actions.edit, Subjects.customFields) ||
    ability.can(Actions.edit, Subjects.importRsims);

  const url = useSimProfilesUrlFromFilters();
  const locationsUrl = useLocationsUrlFromFilters();
  const bulkProvisioningUrl = useBulkProvisioningUrlFromFilters();

  const dashboardUrl = useDashboardUrlFromFilters();

  return (
    <List
      sx={{
        mt: 77,
        '* + *': {
          mt: 2,
        },
      }}
    >
      <NavigationLink
        page={PAGES.DASHBOARD}
        label={t('dashboard.title')}
        data-testid="dashboard-link"
        to={dashboardUrl}
        icon={<DashboardIcon />}
      />
      <NavigationLinkWithInnerNavigationSupport
        page={PAGES.PRODUCTS}
        label={t('common.products')}
        data-testid="products-link"
        navigationAtom={productNavigationAtom}
        icon={<InventoryIcon />}
      />

      <NavigationLink
        label={t('simProfiles.profiles')}
        data-testid="sim-inventory-link"
        to={url}
        icon={<SimCardIcon width={'1.5rem'} height={'1.5rem'} />}
        page={PAGES.INVENTORY}
      />
      {locationsEnabled ? (
        <NavigationLink
          label={'Locations'}
          data-testid="locations-link"
          to={locationsUrl}
          icon={<LocationsIcon width="1.5rem" />}
          page={PAGES.LOCATIONS}
        />
      ) : null}
      {productsManagementEnabled ? (
        activityLogEnabled ? (
          <NavigationLinkWithInnerNavigationSupport
            page={PAGES.ACTIVITY_LOG}
            label={t('bulkProvisioning.title')}
            data-testid="activity-log-link"
            navigationAtom={activityLogNavigationAtom}
            icon={<BPUploadIcon width={'1.5rem'} height={'1.5rem'} />}
          />
        ) : null
      ) : (
        <SimInventoryCan I={Actions.read} a={Subjects.bulkProvisioning}>
          <NavigationLink
            label={t('bulkProvisioning.title')}
            data-testid="provisioning-link"
            to={bulkProvisioningUrl}
            icon={<BPUploadIcon width={'1.5rem'} height={'1.5rem'} />}
            page={PAGES.PROVISIONING}
          />
        </SimInventoryCan>
      )}

      {adminPanelEnabled && (
        <NavigationLinkWithInnerNavigationSupport
          page={PAGES.ADMIN}
          data-testid="admin-link"
          label={t('common.admin')}
          icon={<SettingsIcon />}
          navigationAtom={adminNavigationAtom}
        />
      )}
    </List>
  );
};
